import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import "../../App.css";
import { Utils, fetchWithAuth, handleSessionExpired, formatDate, formatDateTime, getDefaultPageSize, getTotalPages, can, canAny, exportListCsv } from "../../utils";
import ListPaginationBar from "../common/ListPaginationBar";
import { BsPencil, BsClockHistory } from "react-icons/bs";
import { MdAddCircle, MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import Swal from "sweetalert2";

const NOTIFICATION_DAYS_OPTIONS = Array.from({ length: 30 }, (_, i) => ({ value: i + 1, label: `${i + 1} day${i !== 0 ? "s" : ""}` }));

const STATUS_COLORS = {
  1: "#22c55e",
  2: "#eab308",
  3: "#ef4444",
  4: "#6b7280",
};

const SiteCheck = () => {
  const [userData] = useState(() => JSON.parse(localStorage.getItem("userData")));
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [form, setForm] = useState({
    url: "",
    label: "",
    client_name: "",
  });
  const [editForm, setEditForm] = useState({ url: "", label: "", client_name: "", status: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [notificationSetting, setNotificationSetting] = useState({
    email_enabled: true,
    notification_emails: "",
    notification_days: [],
    site_down_alerts_enabled: false,
  });
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [showNotificationSection, setShowNotificationSection] = useState(false);
  const [cronRunning, setCronRunning] = useState(false);
  const tableInnerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const pageSize = getDefaultPageSize();
  const [currentPage, setCurrentPage] = useState(1);

  const getSiteActionOptionCount = () =>
    1 + (can("sites.edit") ? 1 : 0) + (can("sites.delete") ? 1 : 0);
  const { openActionItem, dropdownPosition, dropdownRef, closeDropdown, handleOpenDropdown } = useActionDropdown({
    tableScrollRef: tableContainerRef,
    optionCount: getSiteActionOptionCount,
    menuWidth: 200,
  });
  const isAtScrollEnd = useScrollEndDetection(tableContainerRef, tableInnerRef, sites?.length);
  const tableMaxHeight = useTableMaxHeight(tableContainerRef);

  const fetchSites = useCallback(() => {
    if (!userData?.id) return;
    setLoading(true);
    fetchWithAuth(Utils.baseurl + "ssl-domain/sites", {
      method: "POST",
      body: JSON.stringify({ userId: userData.id }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setLoading(false);
        if (handleSessionExpired(res, result)) return;
        if (result.status) setSites(result.data || []);
        else toast.error(result.message || "Failed to load sites");
      })
      .catch((err) => {
        setLoading(false);
        toast.error("Network error");
      });
  }, [userData?.id]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const fetchNotificationSetting = useCallback(() => {
    if (!userData?.id) return;
    fetchWithAuth(Utils.baseurl + "ssl-domain/notification-settings", {
      method: "POST",
      body: JSON.stringify({ userId: userData.id }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (handleSessionExpired(res, result)) return;
        if (result.status && result.data) {
          const days = result.data.notification_days;
          setNotificationSetting({
            email_enabled: result.data.email_enabled !== false,
            notification_emails: result.data.notification_emails || "",
            notification_days: Array.isArray(days) ? days : [],
            site_down_alerts_enabled: result.data.site_down_alerts_enabled === true,
          });
        }
      })
      .catch(() => { });
  }, [userData?.id]);

  useEffect(() => {
    fetchNotificationSetting();
  }, [fetchNotificationSetting]);

  const handleNotificationSave = (e) => {
    e.preventDefault();
    setNotificationSaving(true);
    fetchWithAuth(Utils.baseurl + "ssl-domain/notification-settings/update", {
      method: "POST",
      body: JSON.stringify({
        userId: userData.id,
        email_enabled: notificationSetting.email_enabled,
        notification_emails: notificationSetting.notification_emails,
        notification_days: notificationSetting.notification_days,
        site_down_alerts_enabled: notificationSetting.site_down_alerts_enabled,
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setNotificationSaving(false);
        if (handleSessionExpired(res, result)) return;
        if (result.status) {
          toast.success(result.message || "Notification settings saved.");
          fetchNotificationSetting();
          setShowNotificationSection(false);
        } else toast.error(result.message || "Failed");
      })
      .catch(() => setNotificationSaving(false));
  };

  const handleRunCronNow = () => {
    setCronRunning(true);
    fetchWithAuth(Utils.baseurl + "ssl-domain/cron/run", {
      method: "POST",
      body: JSON.stringify({ userId: userData.id }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setCronRunning(false);
        if (handleSessionExpired(res, result)) return;
        if (result.status) toast.success(result.message || "Cron run completed.");
        else toast.error(result.message || "Cron run failed.");
        fetchSites();
      })
      .catch(() => {
        setCronRunning(false);
        toast.error("Cron run failed");
      });
  };

  const handleSendTestEmail = () => {
    setTestEmailSending(true);
    fetchWithAuth(Utils.baseurl + "ssl-domain/notification-settings/send-test", {
      method: "POST",
      body: JSON.stringify({ userId: userData.id }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setTestEmailSending(false);
        if (handleSessionExpired(res, result)) return;
        if (result.status) toast.success(result.message || "Test email sent.");
        else toast.error(result.message || "Failed to send test email.");
      })
      .catch(() => setTestEmailSending(false));
  };

  const handleAdd = () => {
    setForm({ url: "", label: "", client_name: "" });
    setShowAddModal(true);
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (!form.url.trim()) {
      toast.error("URL is required");
      return;
    }
    setSubmitting(true);
    fetchWithAuth(Utils.baseurl + "ssl-domain/sites/add", {
      method: "POST",
      body: JSON.stringify({
        userId: userData.id,
        url: form.url.trim(),
        label: form.label.trim() || null,
        client_name: form.client_name.trim() || null,
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setSubmitting(false);
        if (handleSessionExpired(res, result)) return;
        if (result.status) {
          toast.success(result.message || "Site added.");
          setShowAddModal(false);
          fetchSites();
        } else toast.error(result.message || "Failed");
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Request failed");
      });
  };

  const openEdit = (site) => {
    setEditingSite(site);
    setEditForm({
      url: site.url,
      label: site.label || "",
      client_name: site.client_name || "",
      status: site.status,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editingSite || !editForm.url.trim()) return;
    setSubmitting(true);
    fetchWithAuth(Utils.baseurl + "ssl-domain/sites/update", {
      method: "POST",
      body: JSON.stringify({
        userId: userData.id,
        siteId: editingSite.id,
        url: editForm.url.trim(),
        label: editForm.label.trim() || null,
        client_name: editForm.client_name.trim() || null,
        status: editForm.status,
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setSubmitting(false);
        if (handleSessionExpired(res, result)) return;
        if (result.status) {
          toast.success(result.message || "Site updated.");
          setShowEditModal(false);
          setEditingSite(null);
          fetchSites();
        } else toast.error(result.message || "Failed");
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Request failed");
      });
  };

  const handleDelete = async (site) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Remove this site from monitoring?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove",
    });
    if (!confirm.isConfirmed) return;
    fetchWithAuth(Utils.baseurl + "ssl-domain/sites/delete", {
      method: "POST",
      body: JSON.stringify({ userId: userData.id, siteId: site.id }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (handleSessionExpired(res, result)) return;
        if (result.status) {
          toast.success(result.message || "Site removed.");
          fetchSites();
        } else toast.error(result.message || "Failed");
      })
      .catch(() => toast.error("Request failed"));
  };

  const openHistory = (site) => {
    fetchWithAuth(Utils.baseurl + "ssl-domain/sites/history", {
      method: "POST",
      body: JSON.stringify({ userId: userData.id, siteId: site.id }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (handleSessionExpired(res, result)) return;
        if (result.status) setHistoryData(result.data);
        else toast.error(result.message || "Failed to load history");
      })
      .catch(() => toast.error("Request failed"));
    setShowHistoryModal(true);
  };

  const closeHistory = () => {
    setShowHistoryModal(false);
    setHistoryData(null);
  };

  const displayName = (site) => site.label || site.domain || site.url;
  const totalRows = sites?.length || 0;
  const totalPages = getTotalPages(totalRows, pageSize);
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const visibleSites = sites.slice(startIdx, endIdx);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("ssl-domain/sites", {
        userId: userData.id,
      }, "ssl-sites.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };


  return (
    <div className="dashboard_right ns-support-list">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>SSL & Domain Monitoring</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={fetchSites}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("sites.add") ? (
            <button type="button" className="add_app_btn btn_theme_primary" onClick={handleAdd}>
              <MdAddCircle />
              <span>Add site</span>
            </button>
            ) : null}
            {canAny(["sites.notification_settings.view", "sites.notification_settings.manage"]) ? (
            <button
              type="button"
              className="add_app_btn_secondary btn_theme_secondary"
              onClick={() => setShowNotificationSection(true)}
            >
              Notification settings
            </button>
            ) : null}
            {canAny(["sites.add", "sites.edit", "sites.change_status", "sites.delete"]) ? (
            <button
              type="button"
              className="add_app_btn_secondary btn_theme_orange"
              onClick={handleRunCronNow}
              disabled={cronRunning}
              title="Check all sites now and send report email"
            >
              {cronRunning ? "Running…" : "Check now"}
            </button>
            ) : null}
          </div>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableContainerRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? 'at-scroll-end' : ''}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : 'calc(100vh - 320px)',
                  overflowY: 'auto',
                  overflowX: 'auto',
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Domain / Label</th>
                      <th>SSL</th>
                      <th>Domain</th>
                      <th>Last check</th>
                      <th className="col-action" style={{ textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", padding: "24px" }}>
                          Loading…
                        </td>
                      </tr>
                    ) : sites.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", padding: "24px" }}>
                          No sites yet. Add a site to monitor SSL and domain expiry.
                        </td>
                      </tr>
                    ) : (
                      visibleSites.map((site) => (
                        <tr key={site.id}>
                          <td>
                            <strong>{displayName(site)}</strong>
                            {site.url && site.url !== displayName(site) && (
                              <div style={{ fontSize: "12px", color: "#6b7280" }}>{site.url}</div>
                            )}
                          </td>
                          <td>
                            {site.ssl_status ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <div style={{ display: "flex", alignItems: "baseline" }}>
                                  <span className="text-muted small" style={{ minWidth: 58, flexShrink: 0 }}>Status:</span>
                                  <span style={{ color: STATUS_COLORS[site.ssl_status.status] || "#000" }}>
                                    {site.ssl_status.status_label}
                                  </span>
                                </div>
                                <div className="small" style={{ display: "flex", alignItems: "baseline" }}>
                                  <span className="text-muted" style={{ minWidth: 58, flexShrink: 0 }}>Expires:</span>
                                  {site.ssl_status.valid_until
                                    ? `${formatDate(site.ssl_status.valid_until)}${site.ssl_status.days_until_expiry != null ? ` (${site.ssl_status.days_until_expiry}d)` : ""}`
                                    : site.ssl_status.days_until_expiry != null
                                      ? `${site.ssl_status.days_until_expiry}d`
                                      : "—"}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {site.domain_status ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <div style={{ display: "flex", alignItems: "baseline" }}>
                                  <span className="text-muted small" style={{ minWidth: 58, flexShrink: 0 }}>Status:</span>
                                  <span style={{ color: STATUS_COLORS[site.domain_status.status] || "#000" }}>
                                    {site.domain_status.status_label}
                                  </span>
                                </div>
                                <div className="small" style={{ display: "flex", alignItems: "baseline" }}>
                                  <span className="text-muted" style={{ minWidth: 58, flexShrink: 0 }}>Expires:</span>
                                  {site.domain_status.expiry_date
                                    ? `${formatDate(site.domain_status.expiry_date)}${site.domain_status.days_until_expiry != null ? ` (${site.domain_status.days_until_expiry}d)` : ""}`
                                    : site.domain_status.days_until_expiry != null
                                      ? `${site.domain_status.days_until_expiry}d`
                                      : "—"}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {site.last_ssl_check_at
                              ? formatDateTime(site.last_ssl_check_at)
                              : "—"}
                          </td>
                          <td className="col-action" style={{ position: "relative", textAlign: "right" }}>
                            <button
                              type="button"
                              className="tabButton"
                              onClick={(e) => handleOpenDropdown(e, site.id, site)}
                            >
                              <svg viewBox="-4 0 16 16" fill="currentColor">
                                <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" fill="currentColor" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            <ListPaginationBar
              page={safePage}
              setPage={setCurrentPage}
              total={totalRows}
              pageSize={pageSize}
              totalPages={totalPages}
              ariaLabel="SiteCheck pagination"
            />
            </div>
          </div>
        </div>
      </div>

      {openActionItem && dropdownPosition && createPortal(
        <div ref={dropdownRef} style={{ position: 'fixed', left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 10000, minWidth: '220px' }}>
          <div className="dropdown-menu-subscription">
            <button type="button" onClick={() => { closeDropdown(); openHistory(openActionItem); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
              <BsClockHistory size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
              <span style={{ fontSize: '0.875rem' }}>History</span>
            </button>
            {can("sites.edit") ? (
            <button type="button" onClick={() => { closeDropdown(); openEdit(openActionItem); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
              <BsPencil size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
              <span style={{ fontSize: '0.875rem' }}>Edit</span>
            </button>
            ) : null}
            {can("sites.delete") ? (
            <button type="button" className="delete-class" onClick={() => { closeDropdown(); handleDelete(openActionItem); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', color: 'red' }}>
              <RxCross2 size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
              <span style={{ fontSize: '0.875rem' }}>Remove</span>
            </button>
            ) : null}
          </div>
        </div>,
        document.body
      )}

      {/* Add site – Bootstrap Modal */}
      <Modal show={showAddModal} onHide={() => !submitting && setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add site</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleAddSubmit}>
          <Modal.Body>
            <div className="form_group">
              <label>URL *</label>
              <input
                type="text"
                className="form_control"
                placeholder="https://example.com"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              />
            </div>
            <div className="form_group">
              <label>Label (optional)</label>
              <input
                type="text"
                className="form_control"
                placeholder="My site"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>
            <div className="form_group">
              <label>Client name (optional)</label>
              <input
                type="text"
                className="form_control"
                value={form.client_name}
                onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn_theme_secondary" style={{ marginRight: "8px" }} onClick={() => !submitting && setShowAddModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn_theme_primary" disabled={submitting}>
              {submitting ? "Adding…" : "Add"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Edit site – Bootstrap Modal */}
      <Modal show={showEditModal && !!editingSite} onHide={() => !submitting && setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit site</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleEditSubmit}>
          <Modal.Body>
            <div className="form_group">
              <label>URL *</label>
              <input
                type="text"
                className="form_control"
                placeholder="https://example.com"
                value={editForm.url}
                onChange={(e) => setEditForm((f) => ({ ...f, url: e.target.value }))}
              />
            </div>
            <div className="form_group">
              <label>Label (optional)</label>
              <input
                type="text"
                className="form_control"
                value={editForm.label}
                onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>
            <div className="form_group">
              <label>Client name (optional)</label>
              <input
                type="text"
                className="form_control"
                value={editForm.client_name}
                onChange={(e) => setEditForm((f) => ({ ...f, client_name: e.target.value }))}
              />
            </div>
            <div className="form_group">
              <label>
                <input
                  type="checkbox"
                  checked={!!editForm.status}
                  disabled={!can("sites.change_status")}
                  onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.checked ? 1 : 0 }))}
                />
                {" "}Active
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn_theme_secondary" style={{ marginRight: "8px" }} onClick={() => !submitting && setShowEditModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn_theme_primary" disabled={submitting}>
              {submitting ? "Saving…" : "Save"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Notification settings – Bootstrap Modal */}
      <Modal show={showNotificationSection} onHide={() => setShowNotificationSection(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Notification settings</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleNotificationSave}>
          <Modal.Body>
            <p style={{ color: "#6b7280", fontSize: "14px", marginBottom: "16px" }}>
              Get email alerts for your monitored sites. Add extra emails (comma-separated) to receive the same report.
            </p>
            <div className="form_group">
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={notificationSetting.email_enabled}
                  onChange={(e) => setNotificationSetting((s) => ({ ...s, email_enabled: e.target.checked }))}
                />
                Enable email alerts
              </label>
            </div>
            <div className="form_group">
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={notificationSetting.site_down_alerts_enabled}
                  onChange={(e) => setNotificationSetting((s) => ({ ...s, site_down_alerts_enabled: e.target.checked }))}
                />
                Notify me when a site is down (unreachable)
              </label>
              <small className="text-muted" style={{ display: "block", marginTop: "4px" }}>
                We ping your sites periodically; if any don&apos;t respond, you&apos;ll get an email. Off by default.
              </small>
            </div>
            <div className="form_group">
              <label>Notify me when SSL or Domain expires in (days)</label>
              <Select
                isMulti
                options={NOTIFICATION_DAYS_OPTIONS}
                value={NOTIFICATION_DAYS_OPTIONS.filter((o) => notificationSetting.notification_days.includes(o.value))}
                onChange={(selected) =>
                  setNotificationSetting((s) => ({
                    ...s,
                    notification_days: (selected || []).map((o) => o.value).sort((a, b) => b - a),
                  }))
                }
                placeholder="Select days (e.g. 30, 15, 7)"
                styles={{
                  control: (base) => ({ ...base, minHeight: "38px" }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
            <div className="form_group">
              <label>Extra emails (comma-separated)</label>
              <input
                type="text"
                className="form_control"
                placeholder="other@example.com, team@example.com"
                value={notificationSetting.notification_emails}
                onChange={(e) => setNotificationSetting((s) => ({ ...s, notification_emails: e.target.value }))}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn_theme_secondary" style={{ marginRight: "8px" }} onClick={() => setShowNotificationSection(false)}>
              Close
            </button>
            {can("sites.notification_settings.manage") ? (
            <>
            <button
              type="button"
              className="btn_theme_orange"
              onClick={handleSendTestEmail}
              disabled={testEmailSending}
            >
              {testEmailSending ? "Sending…" : "Send test email"}
            </button>
            <button type="submit" className="btn_theme_primary" disabled={notificationSaving}>
              {notificationSaving ? "Saving…" : "Save"}
            </button>
            </>
            ) : null}
          </Modal.Footer>
        </form>
      </Modal>

      {/* History modal – scroll only on inner body div, header/footer fixed */}
      <Modal show={showHistoryModal} onHide={closeHistory} size="lg" centered className="history-modal-no-scroll">
        <Modal.Header closeButton>
          <Modal.Title>Check history {historyData?.site?.label ? `– ${historyData.site.label}` : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="">
          <div className="history-modal-body-inner">
            {historyData && (
              <>
                <h5 style={{ marginTop: 0, padding: "0 1rem", paddingTop: "1rem" }}>SSL checks</h5>
                <table className="table_outer">
                  <thead>
                    <tr>
                      <th>Checked at</th>
                      <th>Status</th>
                      <th>Days left</th>
                      <th>Valid until</th>
                      <th>Issuer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.ssl_logs?.length
                      ? historyData.ssl_logs.map((log) => (
                        <tr key={log.id}>
                          <td>{log.checked_at ? formatDateTime(log.checked_at) : "—"}</td>
                          <td style={{ color: STATUS_COLORS[log.status] }}>{log.status_label}</td>
                          <td>{log.days_until_expiry ?? "—"}</td>
                          <td>{log.valid_until ? formatDate(log.valid_until) : "—"}</td>
                          <td width={200}>{log.issuer || "—"}</td>
                        </tr>
                      ))
                      : (
                        <tr>
                          <td colSpan="5">No SSL checks yet.</td>
                        </tr>
                      )}
                  </tbody>
                </table>
                <hr />
                <h5 style={{ marginTop: "24px", padding: "0 1rem" }}>Domain checks</h5>
                <table className="table_outer">
                  <thead>
                    <tr>
                      <th>Checked at</th>
                      <th>Status</th>
                      <th>Days left</th>
                      <th>Expiry date</th>
                      <th>Registrar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.domain_logs?.length
                      ? historyData.domain_logs.map((log) => (
                        <tr key={log.id}>
                          <td>{log.checked_at ? formatDateTime(log.checked_at) : "—"}</td>
                          <td style={{ color: STATUS_COLORS[log.status] }}>{log.status_label}</td>
                          <td>{log.days_until_expiry ?? "—"}</td>
                          <td>{log.expiry_date ? formatDate(log.expiry_date) : "—"}</td>
                          <td width={200}>{log.registrar || "—"}</td>
                        </tr>
                      ))
                      : (
                        <tr>
                          <td colSpan="5">No domain checks yet.</td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn_theme_secondary" onClick={closeHistory}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SiteCheck;
